
import { ActionIcon, Button, Card, Group, Paper, Stack, Switch, Text, useMantineTheme } from '@mantine/core';
import { CheckCircle, FileText, Upload, X } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { FaArrowLeft, FaEye } from 'react-icons/fa6';
import InputField, { InputFieldTypes } from './input_field';

interface Attribute {
  key: string;
  title: string;
}

interface CSVListManagerProps {
  attributes: Attribute[];
  loading?: boolean;
  onUpload: (data: { items: Record<string, string>[] }) => void;
}

interface ParsedCSV {
  headers: string[];
  rows: string[][];
  hasHeader: boolean;
}

const CSVListManager: React.FC<CSVListManagerProps> = ({
  attributes,
  loading = false,
  onUpload,
}) => {
  const theme = useMantineTheme();
  const [file, setFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<ParsedCSV | null>(null);
  const [hasHeader, setHasHeader] = useState(true);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [enabledAttributes, setEnabledAttributes] = useState<Record<string, boolean>>(
    attributes.reduce((acc, attr) => ({ ...acc, [attr.key]: true }), {})
  );
  const [dragOver, setDragOver] = useState(false);

  const inputFileRef = React.useRef<HTMLInputElement | null>(null);

  const parseCSV = useCallback((content: string): ParsedCSV => {
    const lines = content.split('\n').filter(line => line.trim());
    const rows = lines.map(line => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    });

    const headers = rows[0] || [];
    const dataRows = rows.slice(1);

    return {
      headers,
      rows: dataRows,
      hasHeader: true,
    };
  }, []);

  const handleFileUpload = useCallback((uploadedFile: File) => {
    console.log('File uploaded:', uploadedFile.name);
    setFile(uploadedFile);
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      console.log('File content loaded, length:', content.length);
      const parsed = parseCSV(content);
      console.log('Parsed CSV:', parsed);
      setCsvData(parsed);

      // Auto-map columns based on similarity
      const mapping: Record<string, string> = {};
      attributes.forEach(attr => {
        const similarHeader = parsed.headers.find(header =>
          header.toLowerCase().includes(attr.title.toLowerCase()) ||
          attr.title.toLowerCase().includes(header.toLowerCase())
        );
        if (similarHeader) {
          mapping[attr.key] = similarHeader;
        }
      });
      console.log('Auto-mapping:', mapping);
      setColumnMapping(mapping);
    };
    reader.onerror = (error) => {
      console.error('File reading error:', error);
    };
    reader.readAsText(uploadedFile);
  }, [attributes, parseCSV]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    console.log('Files dropped:', files.length);
    if (files.length > 0 && files[0].type === 'text/csv') {
      handleFileUpload(files[0]);
    }
  }, [handleFileUpload]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('File input changed');
    const files = e.target.files;
    if (files && files.length > 0) {
      console.log('Selected file:', files[0].name);
      handleFileUpload(files[0]);
    }
    // Reset the input value to allow selecting the same file again
    e.target.value = '';
  }, [handleFileUpload]);

  const firstItem = useMemo(() => {
    if (!csvData || csvData.rows.length === 0) return null;
    return hasHeader ? csvData.rows[0] : csvData.headers;
  }, [csvData, hasHeader]);

  const getPreviewData = useCallback(() => {
    if (!csvData) return null;

    const dataRow = hasHeader ? csvData.rows[0] : csvData.headers;
    if (!dataRow) return null;

    const preview: Record<string, string> = {};
    attributes.forEach(attr => {
      if (enabledAttributes[attr.key]) {
        const columnIndex = parseInt(columnMapping[attr.key]);

        preview[attr.key] = dataRow[columnIndex];
      }
    });

    return preview;
  }, [csvData, hasHeader, columnMapping, enabledAttributes, attributes]);

  const handleUpload = useCallback(() => {
    if (!csvData) return;

    const items: Record<string, string>[] = [];
    const dataRows = hasHeader ? csvData.rows : [csvData.headers, ...csvData.rows];

    dataRows.forEach(row => {
      const item: Record<string, string> = {};

      attributes.forEach(attr => {
        if (enabledAttributes[attr.key]) {
          const columnIndex = parseInt(columnMapping[attr.key]);
          item[attr.key] = row[columnIndex];
        }
      });

      items.push(item);
    });

    onUpload({ items });
  }, [csvData, hasHeader, columnMapping, enabledAttributes, attributes, onUpload]);

  const resetUpload = () => {
    setFile(null);
    setCsvData(null);
    setColumnMapping({});
    setEnabledAttributes(attributes.reduce((acc, attr) => ({ ...acc, [attr.key]: true }), {}));
  };

  const previewData = getPreviewData();

  return (
    <div>
      {/* File Upload Section */}
      {!file && (
        <Card style={{ border: '2px dashed gray' }}>
          <Stack
            style={{ textAlign: 'center' }}
            onDrop={handleDrop}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
          >
            <div>
              <Upload className="w-12 h-12" />
            </div>
            <Text size="xl" fw="bold">
              Upload your CSV file
            </Text>
            <Text size="md">
              Drag and drop your CSV file here, or click to browse
            </Text>
            <input
              type="file"
              id="file-upload"
              accept=".csv,text/csv,application/csv"
              onChange={handleFileInput}
              style={{ visibility: 'hidden', position: 'absolute' }}
              ref={inputFileRef}
            />
            <label className="cursor-pointer" htmlFor="file-upload">
              <Button onClick={() => inputFileRef.current?.click()} type="button" leftSection={<Upload className="w-4 h-4 mr-2" />}>
                Choose File
              </Button>
            </label>
          </Stack>
        </Card>
      )}

      {/* File Info & Configuration */}
      {file && csvData && (
        <Card>
          <Card.Section style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <Group>
              <ActionIcon size="lg" onClick={resetUpload}>
                <FaArrowLeft />
              </ActionIcon>
              <div style={{ flex: 1 }}>
                <Text size="lg" w="bold" className="text-lg">{file.name}</Text>
                <Text>
                  {csvData.rows.length + 1} rows • {csvData.headers.length} columns
                </Text>
              </div>
            </Group>
          </Card.Section>
          <Card.Section className="space-y-6" mt="xl">
            <Stack>
              {/* Header Toggle */}
              <Group>
                <Switch
                  checked={hasHeader}
                  onChange={() => setHasHeader(h => !h)}
                  id="header-toggle"
                />
                <Text>
                  First row contains headers
                </Text>
              </Group>

              {/* Column Mapping */}
              <Stack>
                <Text fw="bold" size="xl">Map Columns</Text>
                <Stack>
                  {attributes.map((attr) => (
                    <Paper p="xs" key={attr.key} style={{ backgroundColor: theme.colors.dark[6], borderRadius: 6 }}>
                      <Group>
                        <Switch
                          checked={enabledAttributes[attr.key]}
                          onChange={() =>
                            setEnabledAttributes(prev => ({ ...prev, [attr.key]: !prev[attr.key] }))
                          }
                          id={`enable-${attr.key}`}
                        />
                        <div style={{ flex: 1 }}>
                          <Text>{attr.title}</Text>
                        </div>
                        {(
                          <InputField
                            name="value"
                            value={columnMapping[attr.key] || ""}
                            fieldType={InputFieldTypes.SELECT}
                            onChange={({ value }) =>
                              setColumnMapping(prev => ({ ...prev, [attr.key]: value }))
                            }
                            disabled={!enabledAttributes[attr.key]}
                            options={(
                              csvData.headers.map((_, index) => ({
                                value: `${index}`,
                                label: { ...firstItem }[index] || `Column ${index + 1}`
                              }))
                            )}
                          />
                        )}
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              </Stack>

              {/* Preview */}
              {previewData && Object.keys(previewData).length > 0 && (
                <Stack>
                  <Group>
                    <FaEye />
                    <Text fw="bold" size="xl">Preview (first item)</Text>
                  </Group>
                  <Stack>
                    {Object.entries(previewData).map(([key, value]) => {
                      const attr = attributes.find(a => a.key === key);
                      return (
                        <Paper p="xs" style={{ backgroundColor: theme.colors.dark[6] }}>
                          <Group key={key} justify='space-between'>
                            <Text>
                              {attr?.title}:
                            </Text>
                            <Text>
                              {value}
                            </Text>
                          </Group>
                        </Paper>
                      );
                    })}
                  </Stack>
                </Stack>
              )}

              {/* Upload Button */}
              <Group justify='flex-end'>
                <Button
                  onClick={handleUpload}
                  disabled={loading || Object.keys(columnMapping).length === 0}
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Import Data
                    </>
                  )}
                </Button>
              </Group>
            </Stack>
          </Card.Section>
        </Card>
      )}
    </div>
  );
};

export default CSVListManager;
